'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import SocialShareButtons from '@/components/common/SocialShareButtons';
import { generateSignalCard } from '@/services/social-card-generator.service';
import { shareSignal } from '@/services/social-sharing.service';
import { toDisplayFormat } from '@/utils/symbol-mapper.util';

interface Signal {
  id: string;
  pair: string;
  type: 'BUY' | 'SELL';
  entryPrice: number;
  targetPrice: number;
  stopLoss: number;
  confidence: number;
  timeframe: string;
  status: 'ACTIVE' | 'COMPLETED' | 'STOPPED';
  profit?: number;
  timestamp: string;
  isLocked?: boolean; // Locked signal requiring subscription
  requiresSubscription?: boolean; // Alternative naming for clarity
}

interface SignalCardProps {
  signal: Signal;
}

const SignalCard = ({ signal }: SignalCardProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const handleShare = async (platform: 'twitter' | 'discord' | 'telegram') => {
    try {
      // Generate social card image
      const imageData = await generateSignalCard({
        pair: signal.pair,
        type: signal.type,
        confidence: signal.confidence,
        profit: signal.profit,
        status: signal.status,
      });

      // Share to platform
      await shareSignal(
        {
          pair: signal.pair,
          type: signal.type,
          confidence: signal.confidence,
          profit: signal.profit,
        },
        platform,
        imageData
      );

      setShowShareMenu(false);
    } catch (error) {
      console.error('Error sharing signal:', error);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'text-warning bg-warning/10 border-warning/20';
      case 'COMPLETED':
        return 'text-success bg-success/10 border-success/20';
      case 'STOPPED':
        return 'text-error bg-error/10 border-error/20';
      default:
        return 'text-muted-foreground bg-muted border-border';
    }
  };

  const getTypeColor = (type: string) => {
    return type === 'BUY' ? 'text-success' : 'text-error';
  };

  // Safe number formatting with fallback
  const formatPrice = (price: number | undefined | null): string => {
    return price != null ? price.toFixed(2) : '0.00';
  };

  const formatProfit = (profit: number | undefined | null): string => {
    if (profit == null) return '0.00';
    return profit >= 0 ? `+${profit.toFixed(2)}` : profit.toFixed(2);
  };

  // Check if signal is locked (requires subscription)
  const isSignalLocked = signal.isLocked || signal.requiresSubscription || false;

  if (!isHydrated) {
    return (
      <div className="bg-card border border-border rounded-lg p-4 sm:p-6 hover:shadow-lg transition-all duration-200">
        <div className="animate-pulse">
          <div className="h-6 bg-muted rounded w-24 mb-4"></div>
          <div className="h-4 bg-muted rounded w-32 mb-2"></div>
          <div className="h-4 bg-muted rounded w-40"></div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-card border border-border rounded-lg p-4 sm:p-6 hover:shadow-lg transition-all duration-200 dark:hover:shadow-cyan-glow relative ${isSignalLocked ? 'opacity-75' : ''}`}>
      {/* Locked overlay for premium signals */}
      {isSignalLocked && (
        <div className="absolute inset-0 bg-background/60 backdrop-blur-sm rounded-lg flex items-center justify-center z-10">
          <div className="text-center p-6">
            <Icon name="LockClosedIcon" size={48} className="text-primary mx-auto mb-3" variant="solid" />
            <h4 className="text-lg font-heading font-bold text-foreground mb-2">Premium Signal</h4>
            <p className="text-sm text-muted-foreground mb-4">Subscribe to unlock full signal details</p>
            <button className="px-6 py-2 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90 transition-colors">
              Upgrade Now
            </button>
          </div>
        </div>
      )}

      {/* Locked badge in top left for locked signals */}
      {isSignalLocked && (
        <div className="absolute top-4 left-4 z-20 flex items-center space-x-1 px-2 py-1 bg-primary/20 border border-primary rounded-md">
          <Icon name="LockClosedIcon" size={14} className="text-primary" variant="solid" />
          <span className="text-xs font-semibold text-primary">LOCKED</span>
        </div>
      )}

      {/* Share button in top right */}
      <button
        onClick={() => setShowShareMenu(!showShareMenu)}
        className="absolute top-4 right-4 p-2 rounded-lg hover:bg-muted transition-colors z-20"
        title="Share signal"
        disabled={isSignalLocked}
      >
        <Icon name="ShareIcon" size={20} className={isSignalLocked ? 'text-muted-foreground/50' : 'text-muted-foreground'} />
      </button>

      {/* Share menu dropdown */}
      {showShareMenu && !isSignalLocked && (
        <div className="absolute top-14 right-4 bg-card border border-border rounded-lg p-3 shadow-lg z-30">
          <p className="text-xs text-muted-foreground mb-2">Share this signal:</p>
          <SocialShareButtons onShare={handleShare} size="sm" />
        </div>
      )}

      <div className="flex items-start justify-between mb-4 pr-10">
        <div className="flex items-center space-x-3 flex-wrap">
          <div className={`px-3 py-1 rounded-full text-xs font-semibold ${getTypeColor(signal.type)}`}>
            {signal.type}
          </div>
          <h3 className="text-lg font-heading font-bold text-foreground">
            {toDisplayFormat(signal.pair)}
          </h3>
        </div>
        <div className={`px-3 py-1 rounded-full text-xs font-semibold border whitespace-nowrap ${getStatusColor(signal.status)}`}>
          {signal.status}
        </div>
      </div>

      <div className={`grid grid-cols-2 gap-4 mb-4 ${isSignalLocked ? 'blur-sm' : ''}`}>
        <div>
          <p className="text-xs text-muted-foreground mb-1">Entry Price</p>
          <p className="text-sm font-semibold text-foreground">
            {isSignalLocked ? '•••••' : `$${formatPrice(signal.entryPrice)}`}
          </p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground mb-1">Target Price</p>
          <p className="text-sm font-semibold text-success">
            {isSignalLocked ? '•••••' : `$${formatPrice(signal.targetPrice)}`}
          </p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground mb-1">Stop Loss</p>
          <p className="text-sm font-semibold text-error">
            {isSignalLocked ? '•••••' : `$${formatPrice(signal.stopLoss)}`}
          </p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground mb-1">Timeframe</p>
          <p className="text-sm font-semibold text-foreground">
            {isSignalLocked ? '•••' : signal.timeframe}
          </p>
        </div>
      </div>

      <div className={`flex items-center justify-between pt-4 border-t border-border ${isSignalLocked ? 'blur-sm' : ''}`}>
        <div className="flex items-center space-x-2">
          <Icon name="SparklesIcon" size={16} className="text-primary" variant="solid" />
          <span className="text-sm font-semibold text-primary">
            {isSignalLocked ? '••' : signal.confidence}% Confidence
          </span>
        </div>
        {signal.profit != null && (
          <div className={`text-sm font-semibold ${signal.profit >= 0 ? 'text-success' : 'text-error'}`}>
            {isSignalLocked ? '•••' : formatProfit(signal.profit)}%
          </div>
        )}
      </div>
    </div>
  );
};

export default SignalCard;